import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-helix-blue hover:text-helix-blue/80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <article className="prose prose-lg max-w-none">
            <h1 data-testid="privacy-title" className="text-4xl font-bold text-helix-dark mb-4">
              Privacy & Data Collection Policy
            </h1>
            <p className="text-gray-500 mb-8">Date: March 2026 | Version: 1.0</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Introduction</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              HelixAspire ("we," "our," or "us") is committed to protecting your privacy in accordance with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth). This policy outlines how we collect, use, disclose, and safeguard your personal information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We may collect personal information, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Name, contact details (email, phone number, address)</li>
              <li>Employment or organisational details (where relevant to service delivery)</li>
              <li>Information collected through website interactions, cookies, and analytics tools</li>
              <li>Any information voluntarily provided through forms, communications, or service engagement</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How We Collect Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We collect personal information through:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Website forms (e.g., contact or enquiry forms)</li>
              <li>Email and phone communications</li>
              <li>Cookies and analytics technologies</li>
              <li>Voluntarily submitted documents (e.g., service requests, onboarding information)</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Purpose of Collection</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We collect personal information for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>To deliver OSINT, intelligence, and security-related services</li>
              <li>To respond to enquiries and client communications</li>
              <li>To support operational and strategic intelligence reporting</li>
              <li>To manage contracts and service delivery</li>
              <li>To conduct marketing and promotional activities (with consent)</li>
              <li>To comply with legal and regulatory obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Use and Disclosure of Personal Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We use and disclose personal information only for purposes consistent with this policy, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Sharing with trusted service providers or partners where required for service delivery</li>
              <li>Legal and regulatory compliance</li>
              <li>Internal analysis to improve intelligence products and services</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mb-6">
              We do not sell or rent personal information to third parties. Information is disclosed externally only with consent or where legally required.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Security of Information</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We take reasonable steps to protect your personal information from misuse, loss, unauthorised access, modification, or disclosure. This includes physical, electronic, and administrative safeguards aligned to the sensitivity of intelligence-related information.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Access and Correction</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              You have the right to access and correct any personal information we hold about you. Requests can be made by contacting us using the details below.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Cookies and Analytics</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our website may use cookies and third-party analytics tools to enhance user experience and understand how our services are used. You can control cookie settings through your browser.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Third-Party Websites</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our website may contain links to third-party sites. HelixAspire is not responsible for the privacy practices or content of external websites.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We may update this policy periodically. Updates will be published on our website with a revised date.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              For privacy-related enquiries or concerns, please contact:
            </p>
            <p className="text-gray-600 leading-relaxed mb-2">
              <strong>HelixAspire</strong>
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Email: scott.hardman@helixaspire.com.au
            </p>
            <p className="text-gray-600 leading-relaxed">
              By using our website or engaging our services, you consent to the collection, use, and disclosure of your personal information as outlined in this policy.
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
